import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExternalLink,
  Inbox as InboxIcon,
  Link2,
  MessagesSquare,
  Send,
  ShoppingBag,
  StickyNote,
  Trash2,
  UserPlus,
} from 'lucide-react';
import {
  DEFAULT_DUE_TIME,
  TIME_SLOTS,
  formatMoney,
  relativeTime,
  slotLabel,
  todayLocal,
  type ChannelType,
  type ConversationStatus,
} from '@bakery/shared';

import { api } from '../lib/api.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';

const CHANNEL_LABEL: Record<string, string> = {
  messenger: 'Messenger',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  viber: 'Viber',
  tiktok: 'TikTok',
  sms: 'SMS',
  email: 'Email',
  walk_in: 'Walk-in',
};

export function InboxScreen() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ConversationStatus>('open');

  const list = useQuery({
    queryKey: ['conversations', status],
    queryFn: () => api.inbox.conversations({ status }),
    refetchInterval: 20_000,
  });

  const channels = useQuery({ queryKey: ['channels'], queryFn: api.inbox.channels });
  const connectedCount = channels.data?.filter((c) => c.channel?.status === 'connected').length ?? 0;

  return (
    <div className="flex h-full">
      {/* Thread list ------------------------------------------------------ */}
      <section
        className={cx(
          'flex w-full flex-col border-r border-[var(--color-line)] lg:w-[360px]',
          conversationId && 'hidden lg:flex',
        )}
      >
        <header className="space-y-3 border-b border-[var(--color-line)] px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-lg font-semibold">Inbox</h1>
            <Badge tone={connectedCount > 0 ? 'ok' : 'warn'}>
              {connectedCount} channel{connectedCount === 1 ? '' : 's'} live
            </Badge>
          </div>
          <Tabs
            value={status}
            onChange={setStatus}
            tabs={[
              { value: 'open' as const, label: 'Open', count: list.data?.counts.open },
              { value: 'pending' as const, label: 'Pending', count: list.data?.counts.pending },
              { value: 'resolved' as const, label: 'Resolved' },
            ]}
          />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {list.isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-16" />
              ))}
            </div>
          ) : list.data?.conversations.length === 0 ? (
            <EmptyState
              icon={<InboxIcon className="size-8" />}
              title="No conversations"
              description={
                connectedCount === 0
                  ? 'Connect Messenger, WhatsApp or Viber in Settings and messages will land here.'
                  : 'Nothing waiting in this folder.'
              }
            />
          ) : (
            <ul className="divide-y divide-[var(--color-line)]">
              {list.data?.conversations.map((conversation) => (
                <li key={conversation.id}>
                  <button
                    onClick={() => navigate(`/inbox/${conversation.id}`)}
                    className={cx(
                      'w-full px-4 py-3 text-left transition hover:bg-[var(--color-surface-sunken)]',
                      conversationId === conversation.id && 'bg-[var(--color-accent-soft)]',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-medium">{conversation.contactName}</span>
                      <span className="shrink-0 text-[11px] text-[var(--color-ink-faint)]">
                        {relativeTime(conversation.lastMessageAt)}
                      </span>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-[var(--color-ink-soft)]">
                      {conversation.lastMessagePreview}
                    </p>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Badge tone="neutral">{CHANNEL_LABEL[conversation.channel] ?? conversation.channel}</Badge>
                      <span className="tabular text-[11px] text-[var(--color-ink-faint)]">{conversation.ticketNo}</span>
                      {conversation.unreadCount > 0 && <Badge tone="brand">{conversation.unreadCount} new</Badge>}
                      {conversation.orderId && <Badge tone="info">Order</Badge>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Thread ----------------------------------------------------------- */}
      <section className={cx('min-w-0 flex-1', !conversationId && 'hidden lg:block')}>
        {conversationId ? (
          <Thread conversationId={conversationId} onBack={() => navigate('/inbox')} />
        ) : (
          <EmptyState
            icon={<MessagesSquare className="size-10" />}
            title="Pick a conversation"
            description="Replies, customer history and creating an order all happen here."
          />
        )}
      </section>
    </div>
  );
}

function Thread({ conversationId, onBack }: { conversationId: string; onBack: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  const conversation = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.inbox.conversation(conversationId),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.data?.messages.length]);

  const reply = useMutation({
    mutationFn: () => api.inbox.reply(conversationId, { body: draft, isPrivateNote: isNote }),
    onSuccess: (result) => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });

      if ('delivered' in result && !result.delivered) {
        // Not connected, or the platform has no API. The text is saved either
        // way, and the operator gets a one-tap link to finish in the real app.
        toast.push({
          tone: 'warn',
          title: 'Saved, but not delivered',
          body: result.error ?? undefined,
          action: result.handoffUrl ? { label: 'Open the app to send', href: result.handoffUrl } : undefined,
        });
      }
    },
  });

  const linkCustomer = useMutation({
    mutationFn: () => api.inbox.linkCustomer(conversationId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      toast.push({ tone: 'ok', title: `${result.customer.displayName} added to clients` });
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: ConversationStatus) => api.inbox.update(conversationId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  if (conversation.isLoading || !conversation.data) {
    return <div className="p-4"><Skeleton className="h-full min-h-64" /></div>;
  }

  const data = conversation.data;

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--color-line)] px-4 py-3">
        <Button size="sm" variant="ghost" className="lg:hidden" onClick={onBack}>
          Back
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{data.contactName}</p>
          <p className="text-xs text-[var(--color-ink-soft)]">
            {CHANNEL_LABEL[data.channel] ?? data.channel} · {data.ticketNo}
            {data.customerDisplayName && ` · ${data.customerDisplayName}`}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {!data.customerId && (
            <Button size="sm" icon={<UserPlus className="size-4" />} loading={linkCustomer.isPending} onClick={() => linkCustomer.mutate()}>
              Add as client
            </Button>
          )}
          <Button size="sm" variant="primary" icon={<ShoppingBag className="size-4" />} onClick={() => setOrderOpen(true)}>
            Create order
          </Button>
          <Select
            value={data.status}
            onChange={(event) => setStatus.mutate(event.target.value as ConversationStatus)}
            className="h-9 w-32"
          >
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </Select>
        </div>
      </header>

      {data.linkedOrder && (
        <div className="flex items-center gap-2 border-b border-[var(--color-line)] bg-[var(--color-info-soft)] px-4 py-2 text-sm">
          <Link2 className="size-4 text-[var(--color-info)]" />
          <span>
            Linked to <strong>{data.linkedOrder.orderNo}</strong> — {formatMoney(data.linkedOrder.total)},{' '}
            {data.linkedOrder.status.name}
          </span>
        </div>
      )}

      {/* Messages -------------------------------------------------------- */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {data.messages.map((message) => (
          <div
            key={message.id}
            className={cx('flex', message.direction === 'out' && !message.isPrivateNote ? 'justify-end' : 'justify-start')}
          >
            <div
              className={cx(
                'max-w-[75%] rounded-[var(--radius-lg)] px-3.5 py-2.5 text-sm',
                message.isPrivateNote
                  ? 'border border-dashed border-[var(--color-warn)]/50 bg-[var(--color-warn-soft)] text-[var(--color-ink)]'
                  : message.direction === 'out'
                    ? 'bg-crust-600 text-white'
                    : 'bg-[var(--color-surface-sunken)]',
              )}
            >
              {message.isPrivateNote && (
                <p className="mb-1 flex items-center gap-1 text-[11px] font-medium opacity-70">
                  <StickyNote className="size-3" /> Internal note
                </p>
              )}
              <p className="whitespace-pre-wrap">{message.body}</p>
              <p className={cx('mt-1 text-[11px]', message.direction === 'out' && !message.isPrivateNote ? 'text-white/60' : 'text-[var(--color-ink-faint)]')}>
                {relativeTime(message.sentAt)}
                {message.deliveryStatus === 'failed' && ' · not delivered'}
              </p>
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>

      {/* Composer -------------------------------------------------------- */}
      <footer className="space-y-2 border-t border-[var(--color-line)] px-4 py-3 pb-safe">
        <div className="flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={isNote} onChange={(event) => setIsNote(event.target.checked)} className="size-3.5 accent-[var(--color-warn)]" />
            Internal note
          </label>
          {data.handoffUrl && (
            <a href={data.handoffUrl} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-crust-600 hover:underline">
              <ExternalLink className="size-3" /> Open in {CHANNEL_LABEL[data.channel]}
            </a>
          )}
        </div>

        <div className="flex gap-2">
          <Textarea
            rows={2}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends; Shift+Enter makes a new line, as in every chat app.
              if (event.key === 'Enter' && !event.shiftKey && draft.trim()) {
                event.preventDefault();
                reply.mutate();
              }
            }}
            placeholder={isNote ? 'Note for the team, not sent to the customer…' : 'Write a reply…'}
            className="flex-1 resize-none"
          />
          <Button
            variant="primary"
            className="self-end"
            icon={<Send className="size-4" />}
            loading={reply.isPending}
            disabled={!draft.trim()}
            onClick={() => reply.mutate()}
          >
            Send
          </Button>
        </div>
      </footer>

      {orderOpen && <OrderFromChatModal conversationId={conversationId} onClose={() => setOrderOpen(false)} />}
    </div>
  );
}

/** Build an order without leaving the conversation. */
function OrderFromChatModal({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.catalog.products() });

  const [lines, setLines] = useState<{ productId: string; qty: number }[]>([]);
  const [fulfillment, setFulfillment] = useState('pickup');
  const [dueDate, setDueDate] = useState(todayLocal);
  const [dueTime, setDueTime] = useState(DEFAULT_DUE_TIME.pickup);

  /*
   * Switching between pickup and delivery moves the time to that kind's usual
   * hour — but only until somebody picks one themselves. Overwriting a chosen
   * time because the fulfilment changed would quietly lose a real decision.
   */
  const [timeChosen, setTimeChosen] = useState(false);

  const changeFulfillment = (value: string) => {
    setFulfillment(value);
    if (!timeChosen) setDueTime(DEFAULT_DUE_TIME[value] ?? DEFAULT_DUE_TIME.pickup);
  };

  const total = lines.reduce((sum, line) => {
    const product = products.data?.find((p) => p.id === line.productId);
    return sum + (product?.price ?? 0) * line.qty;
  }, 0);

  const create = useMutation({
    mutationFn: () =>
      api.inbox.createOrder(conversationId, {
        lines,
        fulfillment,
        // `2026-09-22T16:00` with no zone is read as local time, which is the
        // shop's clock — exactly what was picked.
        dueAt: dueDate && dueTime ? new Date(`${dueDate}T${dueTime}`).toISOString() : null,
      }),
    onSuccess: ({ order, notification }) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.push({
        tone: 'ok',
        title: `${order.orderNo} created`,
        body:
          notification?.status === 'sent'
            ? 'Order summary sent to the customer.'
            : (notification?.error ?? undefined),
        action: notification?.handoffUrl ? { label: 'Send the summary', href: notification.handoffUrl } : undefined,
      });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not create', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Order from this conversation"
      description="The customer, the channel and the thread are all linked automatically."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={lines.length === 0} onClick={() => create.mutate()}>
            Create {formatMoney(total)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-5">
          <Field label="Fulfilment" className="min-w-0 sm:col-span-2">
            <Select value={fulfillment} onChange={(event) => changeFulfillment(event.target.value)}>
              <option value="pickup">Pickup</option>
              <option value="delivery">Delivery</option>
            </Select>
          </Field>
          {/*
            * A date input carries its own intrinsic width and a grid item will
            * not shrink below its content, so an even split pushed the pair
            * past the edge of the dialog. This one gets the larger share, and
            * `min-w-0` lets it give way rather than overflow.
            */}
          <Field label="Needed by" className="min-w-0 sm:col-span-3">
            <div className="flex gap-2">
              <Input
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                className="min-w-0 flex-1"
              />
              <Select
                value={dueTime}
                onChange={(event) => {
                  setDueTime(event.target.value);
                  setTimeChosen(true);
                }}
                className="w-32 shrink-0"
              >
                {TIME_SLOTS.map((slot) => (
                  <option key={slot} value={slot}>
                    {slotLabel(slot)}
                  </option>
                ))}
              </Select>
            </div>
          </Field>
        </div>

        <div className="space-y-2">
          {lines.map((line, index) => (
            <div key={index} className="flex gap-2">
              <Select
                value={line.productId}
                onChange={(event) => setLines((c) => c.map((l, i) => (i === index ? { ...l, productId: event.target.value } : l)))}
                className="min-w-0 flex-1"
              >
                {products.data?.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} — {formatMoney(product.price)}
                  </option>
                ))}
              </Select>
              <Input
                type="number"
                min={1}
                value={line.qty}
                onChange={(event) => setLines((c) => c.map((l, i) => (i === index ? { ...l, qty: Number(event.target.value) } : l)))}
                className="tabular w-20 shrink-0 text-center"
              />
              <Button
                variant="ghost"
                aria-label={`Remove ${products.data?.find((p) => p.id === line.productId)?.name ?? 'this item'}`}
                title="Remove this item"
                className="shrink-0 px-2.5 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10"
                onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                icon={<Trash2 className="size-4" />}
              />
            </div>
          ))}
          <Button
            size="sm"
            icon={<ShoppingBag className="size-4" />}
            disabled={!products.data?.length}
            onClick={() => setLines((current) => [...current, { productId: products.data![0]!.id, qty: 1 }])}
          >
            Add an item
          </Button>
        </div>
      </div>
    </Modal>
  );
}
